import { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box, Button, Paper, MenuItem, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography, TablePagination, Chip
} from "@mui/material";
import { APP_PALETTE } from "../theme/palette";
import { cashFlowApi } from "../services/api";
import { formatCurrencyBs, formatDateEsBo } from "../utils/formatters";
import CashMovementDialog from "../components/cashflow/CashMovementDialog";

const CashFlowPage = () => {
  const [cashFlowData, setCashFlowData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadCashFlow = useCallback(async () => {
    setLoading(true);
    try {
      const response = await cashFlowApi.getReport(dateFrom || undefined, dateTo || undefined);
      setCashFlowData(response.data);
    } catch (error) {
      console.error("Error loading cash flow:", error);
      setCashFlowData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadCashFlow();
  }, []);

  const summary = useMemo(() => {
    if (!cashFlowData) return { income: 0, expenses: 0, net: 0 };
    return {
      income: cashFlowData.total_income || 0,
      expenses: cashFlowData.total_expenses || 0,
      net: cashFlowData.net || 0,
    };
  }, [cashFlowData]);

  const usdBalance = cashFlowData?.usd_balance;
  const hasUsd = usdBalance && Number(usdBalance.amount) !== 0;

  useEffect(() => {
    setPage(0);
  }, [typeFilter, search, cashFlowData]);

  const filteredTransactions = useMemo(() => {
    const all = cashFlowData?.details || [];
    const term = search.trim().toLowerCase();
    return all
      .filter((tx) => {
        const matchesType = typeFilter === "all" || tx.type === typeFilter;
        const matchesSearch = !term || tx.description.toLowerCase().includes(term);
        return matchesType && matchesSearch;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [cashFlowData, typeFilter, search]);

  const transactions = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTransactions.slice(start, start + rowsPerPage);
  }, [filteredTransactions, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const summaryCards = [
    {
      label: "Ingresos",
      value: formatCurrencyBs(summary.income),
      color: APP_PALETTE.status.success,
      bg: APP_PALETTE.surfaces.successSoft,
      border: APP_PALETTE.status.success,
    },
    {
      label: "Egresos",
      value: formatCurrencyBs(summary.expenses),
      color: APP_PALETTE.status.error,
      bg: APP_PALETTE.surfaces.errorSoft,
      border: APP_PALETTE.status.error,
    },
    {
      label: "Neto",
      value: formatCurrencyBs(summary.net),
      color: summary.net >= 0 ? APP_PALETTE.status.success : APP_PALETTE.status.error,
      bg: summary.net >= 0 ? APP_PALETTE.surfaces.successSoft : APP_PALETTE.surfaces.errorSoft,
      border: summary.net >= 0 ? APP_PALETTE.status.success : APP_PALETTE.status.error,
    },
    ...(hasUsd
      ? [{
          label: "Dólares en caja",
          value: `$us ${Number(usdBalance.amount).toFixed(2)}`,
          small: true,
          extra: usdBalance.bs_equivalent != null ? `≈ ${formatCurrencyBs(usdBalance.bs_equivalent)} al tipo de cambio de la última compra de USD` : null,
          color: APP_PALETTE.status.info,
          bg: APP_PALETTE.surfaces.infoSoft,
          border: APP_PALETTE.brand.primary,
        }]
      : []),
    {
      label: "Período",
      value: dateFrom || dateTo
        ? `${dateFrom ? formatDateEsBo(dateFrom + "T00:00:00") : "Inicio"} — ${dateTo ? formatDateEsBo(dateTo + "T00:00:00") : "Hoy"}`
        : "Todo el período",
      color: APP_PALETTE.brand.primary,
      bg: APP_PALETTE.surfaces.infoSoft,
      border: APP_PALETTE.brand.primary,
      small: true,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color={APP_PALETTE.text.primary}>
          Flujo de Caja
        </Typography>
        <Button
          variant="contained"
          onClick={() => setDialogOpen(true)}
          sx={{
            width: { xs: "100%", sm: "auto" },
            backgroundColor: APP_PALETTE.brand.primary,
            "&:hover": { backgroundColor: APP_PALETTE.brand.primaryHover },
            borderRadius: 2,
          }}
        >
          + Nuevo movimiento
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
        {summaryCards.map((card) => (
          <Paper
            key={card.label}
            sx={{
              p: 2,
              backgroundColor: card.bg,
              border: `1px solid ${card.border}22`,
              borderRadius: 3,
              boxShadow: "0 1px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Typography variant="caption" color={APP_PALETTE.text.muted} fontWeight={600} display="block" mb={0.5}>
              {card.label}
            </Typography>
            <Typography
              variant={card.small ? "body2" : "h6"}
              fontWeight={700}
              sx={{ color: card.color }}
            >
              {card.value}
            </Typography>
            {card.extra && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                {card.extra}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.04)", overflowX: "auto" }}>
        <Typography variant="h6" fontWeight={700} color={APP_PALETTE.text.primary} mb={0.5}>
          Transacciones
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Detalle de ingresos y egresos en el período seleccionado
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 260 }, minWidth: { xs: 0, sm: 260 }, flex: { xs: "1 1 100%", sm: "0 0 260px" } }}
            placeholder="Buscar por descripción"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <TextField
            select
            size="small"
            label="Tipo"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ width: { xs: "100%", sm: 140 }, minWidth: { xs: 0, sm: 140 }, flex: { xs: "1 1 100%", sm: "0 0 140px" } }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="income">Ingresos</MenuItem>
            <MenuItem value="expense">Egresos</MenuItem>
          </TextField>
          <TextField
            label="Desde"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: { xs: "100%", sm: 180 }, minWidth: { xs: 0, sm: 180 }, flex: { xs: "1 1 100%", sm: "0 0 180px" } }}
          />
          <TextField
            label="Hasta"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: { xs: "100%", sm: 180 }, minWidth: { xs: 0, sm: 180 }, flex: { xs: "1 1 100%", sm: "0 0 180px" } }}
          />
          <Button
            variant="contained"
            onClick={loadCashFlow}
            disabled={loading}
            sx={{
              backgroundColor: APP_PALETTE.brand.primary,
              "&:hover": { backgroundColor: APP_PALETTE.brand.primaryHover },
            }}
          >
            {loading ? "Cargando..." : "Filtrar"}
          </Button>
        </Box>

        <Table size="small" sx={{ minWidth: { xs: 640, md: "auto" } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: APP_PALETTE.text.secondary }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700, color: APP_PALETTE.text.secondary }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 700, color: APP_PALETTE.text.secondary }} align="right">Ingreso</TableCell>
              <TableCell sx={{ fontWeight: 700, color: APP_PALETTE.text.secondary }} align="right">Egreso</TableCell>
              <TableCell sx={{ fontWeight: 700, color: APP_PALETTE.text.secondary }} align="right">Saldo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.length > 0 ? (
              transactions.map((tx, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={{ color: APP_PALETTE.text.secondary, whiteSpace: { xs: "normal", sm: "nowrap" } }}>
                    {formatDateEsBo(tx.date + "T00:00:00")}
                  </TableCell>
                  <TableCell sx={{ color: APP_PALETTE.text.primary }}>
                    {tx.description}
                    {tx.currency === "USD" && (
                      <Chip
                        label="USD"
                        size="small"
                        sx={{ ml: 1, height: 18, fontSize: 11, background: APP_PALETTE.surfaces.infoSoft, color: APP_PALETTE.brand.primary, fontWeight: 600 }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ color: APP_PALETTE.status.success, fontWeight: 600 }}>
                    {tx.type === "income"
                      ? (tx.currency === "USD" ? `$us ${Number(tx.amount).toFixed(2)}` : formatCurrencyBs(tx.amount))
                      : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ color: APP_PALETTE.status.error, fontWeight: 600 }}>
                    {tx.type === "expense"
                      ? (tx.currency === "USD" ? `$us ${Number(tx.amount).toFixed(2)}` : formatCurrencyBs(tx.amount))
                      : "—"}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: tx.currency === "USD" ? APP_PALETTE.text.muted : (tx.balance >= 0 ? APP_PALETTE.status.success : APP_PALETTE.status.error),
                      fontWeight: tx.currency === "USD" ? 400 : 700,
                    }}
                  >
                    {tx.currency === "USD" ? "—" : formatCurrencyBs(tx.balance)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="caption" color="text.secondary">
                    {loading ? "Cargando transacciones..." : "No hay transacciones en este período"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredTransactions.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[15, 25, 50]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      <CashMovementDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={loadCashFlow}
      />
    </Box>
  );
};

export default CashFlowPage;